import { useState, useEffect, useCallback } from 'react';
import { companyAPI } from '../../../services/api';
import { useAuth } from '../../../contexts/AuthContext';

export interface InventoryRecord {
  id: number;
  ingredient: number;
  ingredient_name: string;
  ingredient_unit: string;
  ingredient_category: string;
  ingredient_category_display: string;
  current_stock: string;
  reorder_point: string | null;
  updated_at: string;
}

export type StockStatus = 'ok' | 'low' | 'all';

export function getStockStatus(record: InventoryRecord): 'ok' | 'low' | 'out' {
  const stock = parseFloat(record.current_stock);
  const reorder = record.reorder_point != null ? parseFloat(record.reorder_point) : null;
  if (stock <= 0) return 'out';
  if (reorder != null && stock <= reorder) return 'low';
  return 'ok';
}

export function useCompanyInventory() {
  const { isExpired } = useAuth();
  const [inventory, setInventory] = useState<InventoryRecord[]>([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState<StockStatus>('all');
  const [feedback, setFeedback] = useState({ error: '', success: '' });

  // Adjust stock modal
  const [adjustModal, setAdjustModal] = useState<{
    show: boolean; record: InventoryRecord | null; newStock: string; newReorder: string; saving: boolean;
  }>({ show: false, record: null, newStock: '', newReorder: '', saving: false });

  const fetchInventory = useCallback(async () => {
    try {
      const res = await companyAPI.getInventory();
      setInventory(res.data);
    } catch (err) {
      console.error('Failed to fetch inventory:', err);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchInventory(); }, [fetchInventory]);

  const filtered = inventory.filter(r => {
    const matchSearch = r.ingredient_name.toLowerCase().includes(search.toLowerCase());
    if (!matchSearch) return false;
    if (statusFilter === 'all') return true;
    const s = getStockStatus(r);
    if (statusFilter === 'low') return s === 'low' || s === 'out';
    return true;
  });

  const openAdjust = (record: InventoryRecord) => {
    setAdjustModal({
      show: true,
      record,
      newStock: record.current_stock,
      newReorder: record.reorder_point ?? '',
      saving: false,
    });
  };

  const handleAdjust = async () => {
    if (!adjustModal.record) return;
    setAdjustModal(prev => ({ ...prev, saving: true }));
    try {
      const res = await companyAPI.updateInventoryRecord(adjustModal.record.id, {
        current_stock: adjustModal.newStock,
        reorder_point: adjustModal.newReorder !== '' ? adjustModal.newReorder : null,
      });
      setInventory(prev => prev.map(r => r.id === adjustModal.record!.id ? res.data : r));
      setAdjustModal({ show: false, record: null, newStock: '', newReorder: '', saving: false });
      setFeedback({ error: '', success: 'Stock adjusted.' });
    } catch {
      setAdjustModal(prev => ({ ...prev, saving: false }));
      setFeedback({ error: 'Failed to adjust stock.', success: '' });
    }
  };

  const [syncing, setSyncing] = useState(false);
  const syncInventory = async () => {
    setSyncing(true);
    try {
      const res = await companyAPI.syncInventoryFromReceived();
      const count = res.data.synced_items ?? 0;
      await fetchInventory();
      setFeedback({ error: '', success: count > 0 ? `Synced ${count} item${count !== 1 ? 's' : ''} from received orders.` : 'Everything is already up to date.' });
    } catch {
      setFeedback({ error: 'Sync failed. Please try again.', success: '' });
    } finally {
      setSyncing(false);
    }
  };

  return {
    isExpired,
    inventory,
    loading,
    search,
    setSearch,
    statusFilter,
    setStatusFilter,
    filtered,
    feedback,
    setFeedback,
    adjustModal,
    setAdjustModal,
    openAdjust,
    handleAdjust,
    refetch: fetchInventory,
    syncing,
    syncInventory,
  };
}
